class GalleryWallSet {
  constructor() {
    this.element = document.querySelector('[data-gallery-wall-set]');
    if (!this.element) return;

    this.productCards = this.element.querySelectorAll('.gallery-wall-set__product-card');
    this.optionSelects = this.element.querySelectorAll('.gallery-wall-set__option-select');
    this.addAllBtn = this.element.querySelector('[data-add-all-to-cart]');
    this.totalPriceEl = this.element.querySelector('[data-total-price]');

    // Store product data: productId -> product object
    this.productsData = new Map();
    // Store selected variants: productId -> { variantId, price }
    this.selectedVariants = new Map();

    this.init();
  }

  init() {
    console.log('GalleryWallSet initialized - Option selects found:', this.optionSelects.length);

    // Load embedded product data
    this.productCards.forEach((card) => {
      const dataScript = card.querySelector('.gallery-wall-set__product-data');
      if (dataScript) {
        try {
          const product = JSON.parse(dataScript.textContent);
          this.productsData.set(product.id, product);
          console.log(`Loaded embedded data for product ${product.id}:`, product);
        } catch (error) {
          console.error('Error parsing product data:', error);
        }
      }
    });

    // Initialize custom dropdowns
    this.initCustomDropdowns();

    // Attach event listeners to option selects (hidden native selects)
    this.optionSelects.forEach((select) => {
      select.addEventListener('change', (e) => this.onOptionChange(e));
    });

    // Attach event listener to add all button
    if (this.addAllBtn) {
      this.addAllBtn.addEventListener('click', (e) => this.addAllToCart(e));
    }

    // Trigger price calculation for default selected options
    this.productCards.forEach((card) => {
      this.calculateCardPrice(card);
    });
  }

  initCustomDropdowns() {
    const customDropdowns = this.element.querySelectorAll('.gallery-wall-set__custom-dropdown');

    customDropdowns.forEach((dropdown) => {
      const toggle = dropdown.querySelector('.gallery-wall-set__dropdown-toggle');
      const menu = dropdown.querySelector('.gallery-wall-set__dropdown-menu');
      const options = dropdown.querySelectorAll('.gallery-wall-set__dropdown-option');
      const optionName = dropdown.dataset.optionName;
      const card = dropdown.closest('.gallery-wall-set__product-card');

      // Find corresponding hidden native select
      const nativeSelect = card.querySelector(`.gallery-wall-set__option-select[data-option-name="${optionName}"]`);

      // Toggle dropdown on click
      toggle.addEventListener('click', (e) => {
        e.stopPropagation();
        // Close all other dropdowns first
        this.element.querySelectorAll('.gallery-wall-set__custom-dropdown.open').forEach((d) => {
          if (d !== dropdown) {
            d.classList.remove('open');
            d.classList.remove('open-up');
          }
        });

        // Determine if dropdown should open upward
        const scrollContainer = dropdown.closest('.gallery-wall-set__products');
        if (scrollContainer) {
          const toggleRect = toggle.getBoundingClientRect();
          const containerRect = scrollContainer.getBoundingClientRect();
          const spaceBelow = containerRect.bottom - toggleRect.bottom;
          // If less than 210px below (menu max-height 200px + some padding), open upward
          if (spaceBelow < 210) {
            dropdown.classList.add('open-up');
          } else {
            dropdown.classList.remove('open-up');
          }
        }

        dropdown.classList.toggle('open');
      });

      // Handle option selection
      options.forEach((option) => {
        option.addEventListener('click', (e) => {
          e.stopPropagation();
          const value = option.dataset.value;

          // Update toggle content - include swatch if it's a frame_type dropdown
          const isFrameType = optionName === 'frame_type' || optionName === 'Frame Type';
          if (isFrameType) {
            const swatchClass = value.toLowerCase().replace(/\s+/g, '-');
            toggle.innerHTML = `<span class="gallery-wall-set__swatch gallery-wall-set__swatch--${swatchClass}"></span><span>${value}</span>`;
          } else {
            toggle.textContent = value;
          }
          toggle.classList.add('has-value');

          // Mark selected option
          options.forEach((o) => o.classList.remove('selected'));
          option.classList.add('selected');

          // Update hidden native select
          if (nativeSelect) {
            nativeSelect.value = value;
            nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
          }

          // Close dropdown
          dropdown.classList.remove('open');
        });
      });
    });

    // Close dropdowns when clicking outside
    document.addEventListener('click', () => {
      this.element.querySelectorAll('.gallery-wall-set__custom-dropdown.open').forEach((d) => {
        d.classList.remove('open');
        d.classList.remove('open-up');
      });
    });
  }

  calculateCardPrice(card) {
    const productId = parseInt(card.dataset.productId);

    // Get all selected options for this product
    const optionSelects = card.querySelectorAll('.gallery-wall-set__option-select');
    const selectedOptions = {};

    optionSelects.forEach((sel) => {
      if (sel.value) {
        selectedOptions[sel.dataset.optionName] = sel.value;
      }
    });

    console.log(`Calculating price for product ${productId}:`, selectedOptions);

    // Auto-select size based on frame_type and mount
    const autoSize = this.autoSelectSize(card, selectedOptions);
    
    // Add the auto-selected size to selectedOptions
    if (autoSize) {
      selectedOptions['product_size'] = autoSize;
      selectedOptions['Size'] = autoSize;
    }

    // Update frame styling on product image
    this.updateProductImageFrame(card, selectedOptions);

    // Try to find matching variant
    this.updateVariantForProduct(productId, card, selectedOptions);
  }

  onOptionChange(event) {
    const select = event.target;
    const card = select.closest('.gallery-wall-set__product-card');
    
    this.calculateCardPrice(card);
  }

  updateProductImageFrame(card, selectedOptions) {
    const productImage = card.querySelector('[data-product-image]');
    if (!productImage) {
      console.log('No product image found');
      return;
    }

    const frameType = selectedOptions['frame_type'] || selectedOptions['Frame Type'];
    const mount = selectedOptions['mount'] || selectedOptions['Mount / Passepartout'];

    console.log(`updateProductImageFrame - frameType: "${frameType}", mount: "${mount}"`);

    // Frame should show if frameType is selected, regardless of mount
    if (!frameType) {
      console.log('Frame type not selected, removing data-frame');
      productImage.removeAttribute('data-frame');
      productImage.removeAttribute('data-mount');
      return;
    }

    // Handle specific frame type mappings
    let frameKey = '';
    if (frameType === 'Black Wood') frameKey = 'blackwood';
    else if (frameType === 'White Wood') frameKey = 'whitewood';
    else if (frameType === 'Walnut Wood') frameKey = 'walnutwood';
    else if (frameType === 'Natural Wood') frameKey = 'naturalwood';
    else if (frameType === 'Black Metal') frameKey = 'blackmetal';
    else if (frameType === 'Gold Metal') frameKey = 'goldmetal';
    else if (frameType === 'Canvas Only') frameKey = 'canvas';
    else if (frameType === 'Black Frame') frameKey = 'blackframe';
    else if (frameType === 'White Frame') frameKey = 'whiteframe';
    else if (frameType === 'Natural Frame') frameKey = 'naturalframe';
    else {
      frameKey = frameType.toLowerCase().replace(/\s+/g, '');
    }

    // Handle mount mappings
    let mountKey = 'none';
    if (mount === 'with mount') {
      mountKey = 'withmount';
    } else if (mount === 'no mount') {
      mountKey = 'none';
    } else if (mount) {
      mountKey = mount.toLowerCase().replace(/\s+/g, '');
    }

    // Always set data-frame when frameType is selected
    const dataFrameValue = `${frameKey}-${mountKey}`;
    console.log(`Setting data-frame to: "${dataFrameValue}"`);
    productImage.setAttribute('data-frame', dataFrameValue);

    // Set data-mount attribute separately for padding when mount is "with mount"
    if (mount === 'with mount') {
      console.log(`Setting data-mount to: withmount`);
      productImage.setAttribute('data-mount', 'withmount');
    } else {
      productImage.removeAttribute('data-mount');
    }
    
    // Log the current style to verify it's being applied
    setTimeout(() => {
      const computedStyle = window.getComputedStyle(productImage);
      console.log(`Border after update: ${computedStyle.border}`);
      console.log(`Padding after update: ${computedStyle.padding}`);
    }, 100);
  }

  autoSelectSize(card, selectedOptions) {
    const product = this.productsData.get(parseInt(card.dataset.productId));
    if (!product) {
      console.log('AutoSelectSize - No product data found');
      return null;
    }

    let autoSize = null;

    // Determine auto size based on product type and selected options
    if (product.tags && product.tags.includes('frame')) {
      const frameType = selectedOptions['frame_type'] || selectedOptions['Frame Type'];
      const mount = selectedOptions['mount'] || selectedOptions['Mount / Passepartout'];

      console.log(`Frame product - frameType: ${frameType}, mount: ${mount}`);

      // Wood frames
      if (['Black Wood', 'White Wood', 'Walnut Wood', 'Natural Wood'].includes(frameType)) {
        if (mount === 'with mount') {
          autoSize = '30x40';
        } else if (mount === 'no mount') {
          autoSize = '28x40';
        }
      }
      // Metal frames
      else if (['Black Metal', 'Gold Metal'].includes(frameType)) {
        if (mount === 'with mount') {
          autoSize = '27.5x37.5';
        } else if (mount === 'no mount') {
          autoSize = '25.5x37.5';
        }
      }
    }
    // Canvas products
    else if (product.tags && product.tags.includes('canvas')) {
      const canvasType = selectedOptions['frame_type'] || selectedOptions['Frame Type'];

      console.log(`Canvas product - canvasType: ${canvasType}`);

      if (canvasType === 'Canvas Only') {
        autoSize = '30x45';
      } else if (['Black Frame', 'White Frame', 'Natural Frame'].includes(canvasType)) {
        autoSize = '35x50';
      }
    }

    // Update the size display text
    const sizeDisplay = card.querySelector('[data-size-display]');
    if (sizeDisplay && autoSize) {
      console.log(`Updating size display to: ${autoSize}`);
      sizeDisplay.textContent = autoSize;
    }

    return autoSize;
  }

  updateVariantForProduct(productId, card, selectedOptions) {
    const product = this.productsData.get(productId);
    if (!product) {
      console.log(`No product data for ${productId}`);
      return;
    }

    const priceEl = card.querySelector('[data-product-price]');

    // Filter selectedOptions to only include actual product options (not extra keys)
    const relevantOptions = {};
    product.options.forEach((optionName) => {
      // Check for exact match or case variations
      if (selectedOptions[optionName]) {
        relevantOptions[optionName] = selectedOptions[optionName];
      } else {
        // Try to find case-insensitive match
        const key = Object.keys(selectedOptions).find(
          k => k.toLowerCase() === optionName.toLowerCase()
        );
        if (key) {
          relevantOptions[optionName] = selectedOptions[key];
        }
      }
    });

    console.log(`Looking for matching variant in product ${productId}`);
    console.log('Product options:', product.options);
    console.log('Relevant selected options:', relevantOptions);

    // Check if all required options are selected
    if (Object.keys(relevantOptions).length !== product.options.length) {
      console.log(`Not all options selected for product ${productId}. Have: ${Object.keys(relevantOptions).length}, Need: ${product.options.length}`);
      if (priceEl) priceEl.textContent = '-';
      this.selectedVariants.delete(productId);
      this.updateTotalPrice();
      return;
    }

    // Find matching variant
    const matchingVariant = product.variants.find((variant) => {
      // Build option values from variant
      const variantOptions = {};
      for (let i = 0; i < product.options.length; i++) {
        const optionName = product.options[i];
        const optionKey = `option${i + 1}`;
        variantOptions[optionName] = variant[optionKey];
      }

      console.log('Checking variant options:', variantOptions);

      // Check if all selected options match this variant
      return Object.keys(relevantOptions).every(
        (optionName) => relevantOptions[optionName] === variantOptions[optionName]
      );
    });

    if (matchingVariant) {
      console.log(`Found matching variant for ${productId}:`, matchingVariant);
      this.selectedVariants.set(productId, {
        variantId: matchingVariant.id,
        price: matchingVariant.price,
      });

      // Update price display
      if (priceEl) {
        priceEl.textContent = this.formatPrice(matchingVariant.price);
      }
    } else {
      console.log(`No matching variant found for ${productId}`);
      this.selectedVariants.delete(productId);
      if (priceEl) priceEl.textContent = '-';
    }

    // Update total price
    this.updateTotalPrice();
  }

  updateTotalPrice() {
    let total = 0;

    this.selectedVariants.forEach((data) => {
      total += data.price;
    });

    if (this.totalPriceEl) {
      this.totalPriceEl.textContent = this.formatPrice(total);
    }

    console.log('Total price updated:', total);
  }

  formatPrice(priceInCents) {
    const priceInDirhams = priceInCents / 100;
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
    }).format(priceInDirhams);
  }

  async addAllToCart(event) {
    event.preventDefault();

    console.log('Add all to cart clicked');
    console.log('Selected variants:', this.selectedVariants);
    console.log('Product cards count:', this.productCards.length);

    // Validate all products have selections
    if (this.selectedVariants.size === 0) {
      alert('Please select at least one product');
      return;
    }

    if (this.selectedVariants.size !== this.productCards.length) {
      alert(`Please select all options for all products. Selected: ${this.selectedVariants.size}, Total: ${this.productCards.length}`);
      return;
    }

    this.addAllBtn.disabled = true;
    this.addAllBtn.textContent = 'Adding...';

    try {
      // Add each product to cart
      for (const [productId, data] of this.selectedVariants.entries()) {
        console.log(`Adding product ${productId} with variant ${data.variantId}`);
        await this.addToCart(data.variantId);
        // Delay between requests to avoid rate limiting
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }

      console.log('All products added to cart');

      // Redirect to cart after a delay
      setTimeout(() => {
        window.location.href = '/cart';
      }, 1000);
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('Error adding products to cart. Please try again.');
      this.addAllBtn.disabled = false;
      this.addAllBtn.textContent = 'Add All to Cart';
    }
  }

  async addToCart(variantId) {
    console.log(`addToCart called with variantId: ${variantId}`);
    
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
        }),
      });

      console.log(`Cart add response status: ${response.status}`);
      const responseData = await response.json();
      console.log(`Cart add response data:`, responseData);

      if (!response.ok) {
        throw new Error(`Failed to add item ${variantId} to cart. Status: ${response.status}. Response: ${JSON.stringify(responseData)}`);
      }

      console.log('Added variant', variantId, 'to cart');

      // Dispatch custom event to notify theme
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (error) {
      console.error('Error in addToCart:', error.message);
      throw error;
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new GalleryWallSet();
  });
} else {
  new GalleryWallSet();
}